/**
 * Normalizes a user-supplied attribute name or field key into a stable slug.
 *
 * Rules:
 * - Trim whitespace
 * - Lowercase
 * - Collapse non-alphanumeric characters to `_`
 * - Strip leading/trailing underscores
 * - Truncate to 100 characters
 *
 * Used by both the frontend (CustomerForm, AttributeDesignerPanel) and the
 * backend service to derive a canonical `field_key` from a display name.
 * Living here ensures the rule is the single source of truth.
 */
export function normalizeFieldKey(input) {
    return input
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 100);
}
/**
 * Strips non-digit characters from a phone number and normalises to a
 * bare 10-digit US number string, or `null` when no digits are present.
 *
 * A leading country-code `1` is dropped if the result would otherwise
 * exceed 10 digits (e.g. `+1 (555) 867-5309` → `"5558675309"`).
 *
 * Used by both the frontend input controls and the backend storage layer
 * to ensure a consistent canonical form at rest.
 */
export function normalizePhoneForStorage(value) {
    const digitsOnly = (value ?? '').replace(/\D/g, '');
    if (!digitsOnly)
        return null;
    return digitsOnly.length > 10 && digitsOnly.startsWith('1')
        ? digitsOnly.slice(1, 11)
        : digitsOnly.slice(0, 10);
}
/**
 * Formats a raw phone value into a US display string `(NXX) NXX-XXXX`.
 *
 * Delegates normalisation to `normalizePhoneForStorage` so both functions
 * always agree on what constitutes a valid digit sequence.
 */
export function formatPhoneInput(value) {
    const normalizedDigits = normalizePhoneForStorage(value) ?? '';
    if (normalizedDigits.length === 0)
        return '';
    if (normalizedDigits.length < 4) {
        return normalizedDigits.length === 3 ? `(${normalizedDigits})` : `(${normalizedDigits}`;
    }
    if (normalizedDigits.length < 7) {
        return `(${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3)}`;
    }
    return `(${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3, 6)}-${normalizedDigits.slice(6, 10)}`;
}
export * from './adhoc-sections.js';
export * from './business-function-sequence.js';
export * from './business-process.js';
export * from './netsuite-functional-areas.js';
export * from './cost-breakdown.js';
export * from './currency.js';
export * from './slug.js';
export * from './role-key.js';
export * from './extraction-coercion.js';
export * from './module-role-config.js';
export * from './estimate-render.js';
export * from './document-change-summary.js';
export * from './answer-value.js';
export * from './estimate-scenario.js';
export * from './scope-capture-gate.js';
export * from './contract-recommendation.js';
export * from './estimate-utils.js';
export * from './sow-sections.js';
export * from './version.js';
export * from './tiptap.js';
//# sourceMappingURL=index.js.map